import { appBaseUrl } from '@core/http/api-url.util';
import type { SeoRouteData } from '@core/seo/seo.types';

import type { PublicBlogPostDetail } from '../api/public-blog-api.service';

export type BlogPostArticleJsonLd = {
  '@context': 'https://schema.org';
  '@type': 'Article';
  headline: string;
  description: string;
  url: string;
  datePublished: string;
  dateModified: string;
  author: { '@type': 'Person'; name: string };
  publisher: {
    '@type': 'Organization';
    name: string;
    logo: { '@type': 'ImageObject'; url: string };
  };
  image?: string;
  mainEntityOfPage: string;
};

/** Builds route SEO payload for a published blog post. */
export function buildBlogPostSeoData(post: PublicBlogPostDetail): SeoRouteData {
  const description = post.metaDescription ?? post.excerpt ?? post.title;
  return {
    title: post.metaTitle.replace(/\s*\|\s*Akalan$/i, ''),
    description,
    ogType: 'article',
    image: post.ogImageUrl ?? post.coverImageUrl,
    articlePublishedTime: post.publishedAt,
    articleModifiedTime: post.updatedAt,
    articleAuthor: post.authorName,
    canonicalPath: `/blog/${post.slug}`,
  };
}

/** BLG-39 — noindex when post is missing or draft-only. */
export function buildBlogPostNotFoundSeo(): SeoRouteData {
  return {
    title: 'Post not found',
    description: 'This blog post could not be found.',
    robots: 'noindex, nofollow',
  };
}

/** Article JSON-LD for rich results. */
export function buildBlogPostArticleJsonLd(post: PublicBlogPostDetail): BlogPostArticleJsonLd {
  const base = appBaseUrl();
  const pageUrl = `${base}/blog/${post.slug}`;
  const description = (post.metaDescription ?? post.excerpt ?? post.title).trim();
  const image = post.ogImageUrl ?? post.coverImageUrl;

  const graph: BlogPostArticleJsonLd = {
    '@context': 'https://schema.org',
    '@type': 'Article',
    headline: post.title,
    description,
    url: pageUrl,
    datePublished: post.publishedAt,
    dateModified: post.updatedAt,
    author: { '@type': 'Person', name: post.authorName },
    publisher: {
      '@type': 'Organization',
      name: 'Akalan',
      logo: { '@type': 'ImageObject', url: `${base}/akalan-lockup.svg` },
    },
    mainEntityOfPage: pageUrl,
  };

  if (image) {
    graph.image = image;
  }

  return graph;
}
